import { ComponentProps, ReactElement } from 'react';
import {
  imageManifest,
  GeneratedImageInterface,
} from '../assets/generated/imageManifest';


const findEntry = (
  src: string
): { key: string; entry: GeneratedImageInterface } | undefined => {
  if (/^https?:\/\//.test(src)) return undefined;
  const key = src.match(/^\/images\/(.+)\.(?:png|jpe?g)$/)?.[1];
  if (!key) return undefined;
  const entry = imageManifest[key];

  return entry ? { key, entry } : undefined;
};

export const generatedImagePath = ({
  key,
  entry,
  suffix,
  format,
}: {
  key: string;
  entry: GeneratedImageInterface;
  suffix: number | string;
  format: 'avif' | 'jpg' | 'webp';
}) => `/images/gen/${key}.${entry.hash}-${suffix}.${format}`;

const srcSetFor = ({
  key,
  entry,
  format,
}: {
  key: string;
  entry: GeneratedImageInterface;
  format: 'avif' | 'webp';
}) =>
  entry.widths
    .map((w) => `${generatedImagePath({ key, entry, suffix: w, format })} ${w}w`)
    .join(', ');

export const fallbackImagePath = ({ src }: { src: string }) => {
  const found = findEntry(src);
  if (!found) return src;
  const { key, entry } = found;

  return generatedImagePath({
    key,
    entry,
    suffix: Math.max(...entry.widths),
    format: 'webp',
  });
};

export const ogImageFor = ({
  src,
}: {
  src: string;
}): { path: string; width: number; height: number } | undefined => {
  const found = findEntry(src);
  if (!found) return undefined;
  const { key, entry } = found;
  const width = Math.min(1200, entry.width);

  return {
    path: generatedImagePath({ key, entry, suffix: 'og', format: 'jpg' }),
    width,
    height: Math.round((entry.height * width) / entry.width),
  };
};

export const imagePreloadLink = ({
  src,
  sizes,
}: {
  src: string;
  sizes: string;
}): ComponentProps<'link'> | undefined => {
  const found = findEntry(src);
  if (!found) return undefined;
  const { key, entry } = found;

  return {
    rel: 'preload',
    as: 'image',
    imageSrcSet: srcSetFor({ key, entry, format: 'avif' }),
    imageSizes: sizes,
    fetchPriority: 'high',
  };
};

interface Props {
  src: string;
  alt: string;
  sizes?: string | undefined;
  className?: string | undefined;
  fetchPriority?: 'auto' | 'high' | 'low' | undefined;
  loading?: 'eager' | 'lazy' | undefined;
}

export const ResponsiveImage = ({
  src,
  alt,
  sizes = '100vw',
  className,
  fetchPriority,
  loading,
}: Props): ReactElement => {
  const found = findEntry(src);

  const optionalProps = {
    ...(className !== undefined ? { className } : {}),
    ...(fetchPriority !== undefined ? { fetchPriority } : {}),
    ...(loading !== undefined ? { loading } : {}),
  };

  if (!found) {
    return <img src={src} alt={alt} {...optionalProps} />;
  }

  const { key, entry } = found;

  return (
    <picture className="contents">
      <source
        type="image/avif"
        srcSet={srcSetFor({ key, entry, format: 'avif' })}
        sizes={sizes}
      />
      <img
        src={fallbackImagePath({ src })}
        srcSet={srcSetFor({ key, entry, format: 'webp' })}
        sizes={sizes}
        width={entry.width}
        height={entry.height}
        alt={alt}
        {...optionalProps}
      />
    </picture>
  );
};
